import React, { useCallback, useState } from 'react';

import { DEFAULT_API_KEY_PERMISSIONS } from '@aline/common';
import { Alert, IconButton, InputLabel, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';
import { Copy } from 'lucide-react';
import { AlineButton, AlineCheckbox, AlineDialog, FormikAlineInput } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import {
  apiKeyPermissionGroups,
  getActivePreset,
  getAllPermissions,
  getColumnPermissions,
  getModalTitle,
  getToggledColumn,
  getToggledPermissions,
  getToggledRow,
} from './api-key-permission-utils';

const ACTION_LABELS = ['Read', 'Create', 'Update', 'Delete'] as const;

const useStyles = makeStyles()(theme => {
  const sectionGap = theme.spacing(3);
  const fieldGap = theme.spacing(1);

  return {
    actionButton: {
      minWidth: 'unset',
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      width: 'auto',
    },

    dialogContainer: {
      maxHeight: 'calc(100vh - 64px)',
      width: theme.dialogStyles.mediumDialogWidth,
    },

    dialogContent: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: sectionGap,
      minHeight: 0,
    },

    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
    },

    formActionsGroup: {
      display: 'flex',
      gap: theme.spacing(1),
    },

    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: fieldGap,
    },

    formSectionGrow: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: fieldGap,
      minHeight: 0,
    },

    formSectionsCompact: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(2),
      minHeight: 0,
    },

    inputLabel: {
      color: theme.appColors.iconsType,
      fontSize: 12,
      fontWeight: 600,
      margin: 0,
      textTransform: 'uppercase',
    },

    keyLabel: {
      color: theme.palette.text.primary,
      fontSize: 14,
      fontWeight: 600,
    },

    keyValueWrapper: {
      alignItems: 'center',
      backgroundColor: theme.appColors.background,
      border: `1px solid ${theme.appColors.rule}`,
      borderRadius: 8,
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      minWidth: 0,
      padding: theme.spacing(1, 1.5),
    },

    sectionDescription: {
      color: theme.appColors.secondaryType,
      fontSize: '0.8125rem',
      lineHeight: 1.4,
    },

    sectionHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },

    // Segmented control
    segmentedControl: {
      border: `1px solid ${theme.appColors.rule}`,
      borderRadius: 8,
      display: 'flex',
      overflow: 'hidden',
      width: '100%',
    },

    segmentedOption: {
      '&:hover': {
        backgroundColor: alpha(theme.appColors.primary, 0.04),
      },
      '&:not(:last-child)': {
        borderRight: `1px solid ${theme.appColors.rule}`,
      },
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.appColors.secondaryType,
      cursor: 'pointer',
      flex: 1,
      fontSize: '0.8125rem',
      fontWeight: 500,
      padding: theme.spacing(1, 2),
      textAlign: 'center',
      transition: 'background-color 120ms ease, color 120ms ease',
    },

    segmentedOptionSelected: {
      '&:hover': {
        backgroundColor: alpha(theme.appColors.primary, 0.12),
      },
      backgroundColor: alpha(theme.appColors.primary, 0.08),
      color: theme.appColors.primary,
      fontWeight: 600,
    },

    successDialogContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: sectionGap,
    },

    // Permission table
    table: {
      borderCollapse: 'collapse',
      width: '100%',
    },

    tableBody: {},

    tableCell: {
      padding: theme.spacing(0.25, 0),
      textAlign: 'center',
      width: 84,
    },

    tableCellResource: {
      '&:hover': {
        color: theme.appColors.primary,
      },
      color: theme.appColors.iconsType,
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: 500,
      padding: theme.spacing(0.25, 0.75),
      textAlign: 'left',
      userSelect: 'none',
    },

    tableColumnHeader: {
      '&:hover': {
        color: theme.appColors.primary,
      },
      backgroundColor: theme.appColors.background,
      color: theme.appColors.secondaryType,
      cursor: 'pointer',
      fontSize: '0.625rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      padding: theme.spacing(0.5, 0),
      position: 'sticky',
      textAlign: 'center',
      textTransform: 'uppercase',
      top: 0,
      userSelect: 'none',
      width: 84,
      zIndex: 2,
    },

    tableHead: {},

    tableHeaderRow: {
      backgroundColor: theme.appColors.background,
      borderBottom: `2px solid ${theme.appColors.iconsType}`,
    },

    tableResourceHeader: {
      backgroundColor: theme.appColors.background,
      borderRadius: '4px 0 0 0',
      color: theme.appColors.secondaryType,
      fontSize: '0.625rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      padding: theme.spacing(0.5, 0.75),
      position: 'sticky',
      textAlign: 'left',
      textTransform: 'uppercase',
      top: 0,
      zIndex: 3,
    },

    tableRow: {
      '&:not(:last-child)': {
        borderBottom: `1px solid ${alpha(theme.appColors.rule, 0.6)}`,
      },
    },

    tableRowEven: {
      backgroundColor: alpha(theme.appColors.background, 0.5),
    },

    tableWrapper: {
      flex: 1,
      overflowY: 'auto',
    },
  };
});

interface CreateApiKeyModalProps {
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
}

interface ApiKeyFormValues {
  name: string;
  permissions: string[];
}

const ApiKeyFormSchema = Yup.object<ApiKeyFormValues>({
  name: Yup.string()
    .required('Key name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .matches(
      /^[a-zA-Z0-9\s\-_]+$/,
      'Name can only contain letters, numbers, spaces, hyphens, and underscores'
    ),
  permissions: Yup.array().of(Yup.string().required()).required(),
});

type ModalState = 'form' | 'success';

export const CreateApiKeyModal = React.memo(function CreateApiKeyModal({
  onClose,
  onSuccess,
  open,
}: CreateApiKeyModalProps) {
  const { classes, cx } = useStyles();
  const [createApiKey] = Apollo.useCreateApiKeyMutation();
  const [apiError, setApiError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>('form');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const dispatchKeyCopiedSnack = useDispatchSuccessSnack({
    message: 'API key copied to clipboard.',
  });

  const handleSubmit = useCallback(
    async (values: ApiKeyFormValues, helpers: FormikHelpers<ApiKeyFormValues>) => {
      setApiError(null);

      const { data, error } = await createApiKey({
        variables: {
          input: {
            name: values.name.trim(),
            permissions: values.permissions,
          },
        },
      });

      if (data?.createApiKey) {
        setGeneratedKey(data.createApiKey.value);
        setModalState('success');
        onSuccess();
      }
      if (error) {
        setApiError(
          error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'
        );
        setModalState('form');
        helpers.setSubmitting(false);
      }
    },
    [onSuccess, createApiKey]
  );

  const handleCopy = useCallback(async () => {
    if (!generatedKey) return;

    await navigator.clipboard.writeText(generatedKey);
    dispatchKeyCopiedSnack();
  }, [dispatchKeyCopiedSnack, generatedKey]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const resetModalState = useCallback(() => {
    setTimeout(() => {
      setModalState('form');
      setGeneratedKey(null);
      setApiError(null);
    }, 250);
  }, []);

  function renderModalBody() {
    switch (modalState) {
      case 'success':
        if (!generatedKey) return null;

        return (
          <>
            <div className={classes.successDialogContent}>
              <Alert severity="warning">
                <strong>Important:</strong> Copy your API key now. It will not be shown again after
                closing this dialog.
              </Alert>
              <div className={classes.formSection}>
                <div className={classes.keyLabel}>API Key</div>
                <div className={classes.keyValueWrapper}>
                  <Typography noWrap>{generatedKey}</Typography>
                  <IconButton onClick={handleCopy}>
                    <Copy size={16} strokeWidth={2} />
                  </IconButton>
                </div>
              </div>
              <div className={classes.formActions}>
                <div className={classes.formActionsGroup}>
                  <AlineButton
                    buttonClassName={classes.actionButton}
                    color="primary"
                    variant="contained"
                    onClick={onClose}
                  >
                    Done
                  </AlineButton>
                </div>
              </div>
            </div>
          </>
        );
      case 'form':
        return (
          <Formik<ApiKeyFormValues>
            validateOnBlur
            validateOnChange
            initialValues={{ name: '', permissions: [...DEFAULT_API_KEY_PERMISSIONS] }}
            validationSchema={ApiKeyFormSchema}
            onSubmit={handleSubmit}
          >
            {({
              handleReset,
              handleSubmit: formikHandleSubmit,
              isSubmitting,
              isValid,
              setFieldValue,
              values,
            }) => {
              const activePreset = getActivePreset(values.permissions);
              return (
                <form
                  style={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
                  onReset={handleReset}
                  onSubmit={formikHandleSubmit}
                >
                  <div className={classes.dialogContent}>
                    {apiError && (
                      <Alert severity="error" onClose={() => setApiError(null)}>
                        {apiError}
                      </Alert>
                    )}

                    <div className={classes.formSectionsCompact}>
                      <div className={classes.formSection}>
                        <div className={classes.sectionHeader}>
                          <InputLabel className={classes.inputLabel}>Key Name</InputLabel>
                          <Typography className={classes.sectionDescription}>
                            Used to identify this key in your organization.
                          </Typography>
                        </div>
                        <FormikAlineInput
                          autoFocus
                          data-cy="api-key-name-input"
                          fieldName="name"
                          placeholder="e.g., Salesforce Integration"
                        />
                      </div>

                      <div className={classes.formSectionGrow}>
                        <div className={classes.sectionHeader}>
                          <InputLabel className={classes.inputLabel}>Permissions</InputLabel>
                          <Typography className={classes.sectionDescription}>
                            Use a preset to quickly set access, or customize individual resource
                            permissions.
                          </Typography>
                        </div>
                        <div className={classes.segmentedControl}>
                          <button
                            className={cx(
                              classes.segmentedOption,
                              activePreset === 'read' && classes.segmentedOptionSelected
                            )}
                            type="button"
                            onClick={() =>
                              setFieldValue('permissions', [...DEFAULT_API_KEY_PERMISSIONS])
                            }
                          >
                            Read Only
                          </button>
                          <button
                            className={cx(
                              classes.segmentedOption,
                              activePreset === 'full' && classes.segmentedOptionSelected
                            )}
                            type="button"
                            onClick={() => setFieldValue('permissions', getAllPermissions())}
                          >
                            Full Access
                          </button>
                          <button
                            className={cx(
                              classes.segmentedOption,
                              activePreset === 'none' && classes.segmentedOptionSelected
                            )}
                            type="button"
                            onClick={() => setFieldValue('permissions', [])}
                          >
                            None
                          </button>
                        </div>
                        <div className={classes.tableWrapper}>
                          <table className={classes.table}>
                            <thead className={classes.tableHead}>
                              <tr className={classes.tableHeaderRow}>
                                <th className={classes.tableResourceHeader}>Resource</th>
                                {ACTION_LABELS.map(action => {
                                  const colPerms = getColumnPermissions(action);
                                  const allSelected = colPerms.every(p =>
                                    values.permissions.includes(p)
                                  );

                                  return (
                                    <th
                                      key={action}
                                      className={classes.tableColumnHeader}
                                      onClick={() =>
                                        setFieldValue(
                                          'permissions',
                                          getToggledColumn(values.permissions, action)
                                        )
                                      }
                                    >
                                      <div>
                                        <AlineCheckbox
                                          checked={allSelected}
                                          slotProps={{
                                            htmlInput: {
                                              'aria-label': `Select all ${action}`,
                                              readOnly: true,
                                            },
                                          }}
                                          tabIndex={-1}
                                        />
                                      </div>
                                      {action}
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody className={classes.tableBody}>
                              {apiKeyPermissionGroups.map((group, groupIdx) => (
                                <tr
                                  key={group.label}
                                  className={cx(
                                    classes.tableRow,
                                    groupIdx % 2 === 1 && classes.tableRowEven
                                  )}
                                >
                                  <td
                                    className={classes.tableCellResource}
                                    onClick={() =>
                                      setFieldValue(
                                        'permissions',
                                        getToggledRow(
                                          values.permissions,
                                          group.permissions.map(p => p.value)
                                        )
                                      )
                                    }
                                  >
                                    {group.label}
                                  </td>
                                  {group.permissions.map(permission => (
                                    <td key={permission.value} className={classes.tableCell}>
                                      <AlineCheckbox
                                        checked={values.permissions.includes(permission.value)}
                                        slotProps={{
                                          htmlInput: {
                                            'aria-label': `${group.label} ${permission.label}`,
                                          },
                                        }}
                                        onChange={() =>
                                          setFieldValue(
                                            'permissions',
                                            getToggledPermissions(
                                              values.permissions,
                                              permission.value
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className={classes.formActions}>
                      <div className={classes.formActionsGroup}>
                        <AlineButton
                          buttonClassName={classes.actionButton}
                          color="primary"
                          variant="outlined"
                          onClick={handleClose}
                        >
                          Cancel
                        </AlineButton>
                        <AlineButton
                          buttonClassName={classes.actionButton}
                          color="primary"
                          disabled={!values.name.trim() || !isValid || isSubmitting}
                          loading={isSubmitting}
                          type="submit"
                          variant="contained"
                        >
                          Create
                        </AlineButton>
                      </div>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        );
    }
  }

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.dialogContainer }}
      open={open}
      slotProps={{ transition: { onExit: resetModalState } }}
      title={getModalTitle(modalState)}
      onClose={handleClose}
    >
      {renderModalBody()}
    </AlineDialog>
  );
});
